// Chemin: C:\gestion-copro\src\pages\public\CoproprietePortalPage.tsx
// Portail d'entrée dédié pour chaque copropriété
// Session 54 : Création initiale

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { 
  Shield, 
  LogIn, 
  UserPlus, 
  ArrowLeft,
  Building2,
  Users,
  FileText,
  CheckCircle2
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface CoproprietePortal {
  id: string;
  nom: string;
  slug: string;
  adresse?: string;
  ville?: string;
  codePostal?: string;
  sceauUrl?: string;
  presentation?: string;
  couleurPrimaire?: string;
  nombreLots?: number;
}

// ============================================
// COMPOSANT
// ============================================

const CoproprietePortalPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [copropriete, setCopropriete] = useState<CoproprietePortal | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Charger la copropriété par son slug
  useEffect(() => {
    const loadCopropriete = async () => {
      if (!slug) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, 'coproprietes'),
          where('slug', '==', slug.toLowerCase())
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setNotFound(true);
        } else {
          const doc = snapshot.docs[0];
          const data = doc.data();
          setCopropriete({
            id: doc.id,
            nom: data.nom || 'Copropriété',
            slug: data.slug,
            adresse: data.adresse,
            ville: data.ville,
            codePostal: data.codePostal,
            sceauUrl: data.sceauUrl,
            presentation: data.presentation,
            couleurPrimaire: data.couleurPrimaire || '#1E3A8A',
            nombreLots: data.nombreLots
          });
        }
      } catch (error) {
        console.error('Erreur chargement copropriété:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadCopropriete();
  }, [slug]);

  // Stocker le contexte copropriété pour après login
  const handleAccess = () => {
    if (copropriete) {
      sessionStorage.setItem('pendingCoproprieteId', copropriete.id);
      sessionStorage.setItem('pendingCoproprieteSlug', copropriete.slug);
    }
    navigate('/login');
  };

  const handleInscription = () => {
    if (copropriete) {
      sessionStorage.setItem('pendingCoproprieteId', copropriete.id);
      sessionStorage.setItem('pendingCoproprieteSlug', copropriete.slug);
    }
    navigate('/inscription');
  };

  // Chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  // Copropriété non trouvée
  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="text-red-500" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Copropriété introuvable
          </h1>
          <p className="text-gray-600 mb-6">
            L'adresse que vous avez saisie ne correspond à aucune copropriété enregistrée sur Copro Protect.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft size={18} />
            <span>Retour à l'accueil</span>
          </Link>
        </div>
      </div>
    );
  }

  // Couleur primaire de la copropriété (ou défaut)
  const primaryColor = copropriete?.couleurPrimaire || '#1E3A8A';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec lien retour */}
      <header className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 text-gray-500 hover:text-blue-900 transition-colors text-sm"
        >
          <ArrowLeft size={16} />
          <span>Copro Protect</span>
        </Link>
        <div className="flex items-center gap-2">
          <Shield className="text-emerald-600" size={20} />
          <span className="text-gray-500 text-sm">Espace sécurisé</span>
        </div>
      </header>

      {/* Contenu principal */}
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-16">
        {/* Carte principale */}
        <div className="w-full max-w-lg">
          {/* Sceau / Logo */}
          <div className="flex justify-center mb-6">
            {copropriete?.sceauUrl ? (
              <img
                src={copropriete.sceauUrl}
                alt={`Sceau ${copropriete.nom}`}
                className="w-32 h-32 object-contain drop-shadow-2xl"
              />
            ) : (
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center shadow-2xl"
                style={{ backgroundColor: primaryColor }}
              >
                <Building2 className="text-white" size={48} />
              </div>
            )}
          </div>

          {/* Nom de la copropriété */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-2">
            {copropriete?.nom}
          </h1>

          {/* Adresse */}
          {(copropriete?.adresse || copropriete?.ville) && (
            <p className="text-gray-500 text-center mb-6">
              {copropriete?.adresse}
              {copropriete?.codePostal && `, ${copropriete.codePostal}`}
              {copropriete?.ville && ` ${copropriete.ville}`}
            </p>
          )}

          {/* Carte blanche */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Présentation */}
            {copropriete?.presentation && (
              <div className="p-6 border-b border-gray-100">
                <p className="text-gray-600 text-center">
                  {copropriete.presentation}
                </p>
              </div>
            )}

            {/* Infos rapides */}
            {copropriete?.nombreLots && (
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
                <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} />
                    <span>{copropriete.nombreLots} lots</span>
                  </div>
                </div>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="p-6 space-y-3">
              <button
                onClick={handleAccess}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-white font-semibold transition-all hover:opacity-90 hover:shadow-lg"
                style={{ backgroundColor: primaryColor }}
              >
                <LogIn size={20} />
                <span>Accéder à l'espace</span>
              </button>

              <button
                onClick={handleInscription}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gray-100 rounded-xl text-gray-700 font-semibold hover:bg-gray-200 transition-colors"
              >
                <UserPlus size={20} />
                <span>Créer mon compte</span>
              </button>
            </div>

            {/* Footer card */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center">
                Espace réservé aux copropriétaires et membres du Conseil Syndical
              </p>
            </div>
          </div>

          {/* Fonctionnalités */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <FileText className="text-blue-600" size={20} />
              </div>
              <p className="text-gray-500 text-xs">Documents</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Users className="text-blue-600" size={20} />
              </div>
              <p className="text-gray-500 text-xs">Échanges</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="text-blue-600" size={20} />
              </div>
              <p className="text-gray-500 text-xs">Signalements</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center">
          <p className="text-gray-400 text-sm">
            Propulsé par{' '}
            <Link to="/" className="text-blue-600 hover:text-blue-800 transition-colors">
              Copro Protect
            </Link>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default CoproprietePortalPage;
